// Archive (rebuild.md §4.9, Appendix D, task 2.12): the graphic novel page by page, unlocked by chapter.
// The cover, the Halberd Field Guide and the back cover are always available.
import { useEffect, useState } from 'preact/hooks';
import { isFiled, save } from '@app/state.ts';
import { CHAPTERS } from '@content/chapters.ts';
import { mediaUrl } from '@media/manifest.ts';
import { loadComic, type ComicScript } from '@media/MotionComic.tsx';
import { Button } from '@ui/core.tsx';
import { Screen } from './Shell.tsx';

interface Section {
  id: string;
  label: string;
  pages: number[];
  unlocked: boolean;
}

export function archiveSections(): Section[] {
  const cls = save.value.classroom;
  return [
    { id: 'always', label: 'Cover · Field Guide · Back cover', pages: [1, 65, 66], unlocked: true },
    ...CHAPTERS.map((c) => ({
      id: c.id,
      label: c.order === 0 ? 'Welcome · Prologue' : `${c.label.replace('CHAPTER ', 'Ch ').replace('EPILOGUE', 'Epilogue')} · ${c.title}`,
      pages: Array.from({ length: c.pages[1] - c.pages[0] + 1 }, (_, i) => c.pages[0] + i),
      unlocked: cls || isFiled(c.id),
    })),
  ];
}

export function Archive() {
  const [script, setScript] = useState<ComicScript | null>(null);
  const [failed, setFailed] = useState(false);
  const sections = archiveSections();
  const open = sections.filter((s) => s.unlocked);
  const [sec, setSec] = useState(open[open.length - 1]!.id);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    void loadComic().then((s) => (s ? setScript(s) : setFailed(true)));
  }, []);
  const current = sections.find((s) => s.id === sec)!;
  const pageNo = current.pages[idx] ?? current.pages[0]!;
  const page = script?.pages.find((p) => p.page === pageNo);
  return (
    <Screen title="Archive">
      <div class="archive">
        <nav class="archive-toc" aria-label="Sections">
          {sections.map((s) => (
            <button
              key={s.id}
              type="button"
              class="btn btn-ghost"
              disabled={!s.unlocked}
              aria-current={s.id === sec}
              onClick={() => {
                setSec(s.id);
                setIdx(0);
              }}
            >
              {s.unlocked ? s.label : `🔒 ${s.label}`}
            </button>
          ))}
        </nav>
        <section class="archive-page" aria-label={`Page ${pageNo}`}>
          {failed ? (
            <p class="dim">The pages aren’t available offline yet. Open the Archive once while online, or download a chapter.</p>
          ) : page ? (
            <img src={mediaUrl(page.file)!} alt={`Graphic novel page ${pageNo}. ${page.panels.flatMap((p) => p.text.map((t) => t.text)).join(' ')}`} width={page.width} height={page.height} />
          ) : (
            <p class="dim" aria-busy="true">
              Loading…
            </p>
          )}
          <div class="archive-nav">
            <Button onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0}>
              ← Previous
            </Button>
            <span class="dim">
              Page {pageNo} ({idx + 1} of {current.pages.length})
            </span>
            <Button onClick={() => setIdx(Math.min(current.pages.length - 1, idx + 1))} disabled={idx >= current.pages.length - 1}>
              Next →
            </Button>
          </div>
        </section>
      </div>
    </Screen>
  );
}
